import { useState } from 'react';
import { DateRangeFilter } from '@/components/date-range-filter';
import type { DatePreset } from '@/lib/date-presets';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CreditCard, ChevronLeft, ChevronRight } from 'lucide-react';

interface PaymentWithInvoice {
  id: string;
  invoiceId: string;
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  referenceNumber: string | null;
  notes: string | null;
  invoiceNumber: number | null;
  accountName: string | null;
  businessProfileName: string | null;
}

const methodLabels: Record<string, string> = {
  check: 'Check',
  ach: 'ACH',
  wire: 'Wire',
  credit_card: 'Credit Card',
  cash: 'Cash',
  other: 'Other',
};

export function PaymentsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [datePreset, setDatePreset] = useState<DatePreset>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['payments', { page, dateFrom, dateTo }],
    queryFn: async () => {
      const result = await api.getPayments({
        page,
        limit: 20,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      });
      return result;
    },
  });

  const payments = (data?.data || []) as PaymentWithInvoice[];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6">
      {/* Content rendered within Revenue tabs */}
      <div className="flex gap-4">
        <DateRangeFilter
          preset={datePreset}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onChange={({ preset, dateFrom: f, dateTo: t }) => {
            setDatePreset(preset);
            setDateFrom(f);
            setDateTo(t);
            setPage(1);
          }}
        />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : payments.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CreditCard className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-1">No payments recorded</h3>
            <p className="text-muted-foreground">
              Payments will appear here as you record them against invoices
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-3">
            {payments.map((payment) => (
              <Card
                key={payment.id}
                className="cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => navigate(`/billing/invoices/${payment.invoiceId}`)}
              >
                <CardContent className="flex items-center justify-between py-4">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                      <CreditCard className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <div className="font-medium">{formatCurrency(payment.amount)}</div>
                      <div className="text-sm text-muted-foreground">
                        {payment.accountName}
                        {payment.invoiceNumber && (
                          <span className="font-mono"> - Invoice #{payment.invoiceNumber}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {payment.businessProfileName && (
                      <div className="text-sm text-muted-foreground">
                        {payment.businessProfileName}
                      </div>
                    )}
                    <Badge variant="outline">
                      {methodLabels[payment.paymentMethod] || payment.paymentMethod}
                    </Badge>
                    <div className="text-sm text-muted-foreground w-24 text-right">
                      {formatDate(payment.paymentDate)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {(page - 1) * 20 + 1} to {Math.min(page * 20, pagination.total)} of{' '}
                {pagination.total} payments
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= pagination.totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
